import React from 'react'
import dynamic from 'next/dynamic'
import { observer } from 'mobx-react-lite'
import StoriesStore from 'stores/stories'
import { useStore } from 'hooks'
import { THEME, capitalizeAndDashCase } from 'utils'
import StoryHtml from 'components/StoryHtml'
import StoryMeta from 'components/StoryMeta'
import StoryTags from 'components/StoryTags'
import VideoPlayer from 'components/VideoPlayer'
import Gallery from 'components/Gallery'
import Sidebar from 'components/Sidebar'
import RevcontentWidget from 'components/RevcontentWidget'
import MinuteMediaAd from 'components/MinuteMediaAd'
import LazyLoad from 'components/LazyLoad'

import PodcastPlayer from 'components/PodcastPlayer'
import ClientOnly from 'components/ClientOnly'
const SpotIMPopularInTheCommunity = dynamic(() => import('components/SpotIMPopularInTheCommunity'), { ssr: false })
const StoryComments = dynamic(() => import('components/SpotIMComments'), { ssr: false })

const BlogDetail = ({ adSettings }) => {
  const storiesStore = useStore(StoriesStore)
  const story = storiesStore.current
  const videoSource = storiesStore.currentVideoSource
  const hideComments = !story.comment_status_open

  return (
    <>
      <div className='container'>
        <div className='row row--main'>
          <div className='col col--main'>
            <article className='story'>
              <header className='story__header'>
                <h1 className='story__title'>{story.title}</h1>
                <StoryMeta story={story} placement='top' hideComments={hideComments} />
              </header>
              <div className='story__content'>
                {story.type === 'barstool_original' && videoSource && (
                  <VideoPlayer story={story} videoSource={videoSource} />
                )}
                {story.type === 'podcast' && (
                  <ClientOnly>
                    <PodcastPlayer story={story} />
                  </ClientOnly>
                )}
                {story.type === 'gallery' && <Gallery story={story} adSettings={adSettings} />}
                <StoryHtml story={story} adSettings={adSettings} />
                {story.type === 'standard_post' && <MinuteMediaAd />}
                <StoryTags tags={story.tags} />
              </div>
              <StoryMeta story={story} placement='bottom' hideComments={hideComments} />
            </article>
            <div id='story-comments'>
              {!story.ads_disabled && <RevcontentWidget id={`${capitalizeAndDashCase(story.type)}_Comments-Top`} />}
              <div className='storyComments-container'>
                <LazyLoad offset={500} once>
                  <SpotIMPopularInTheCommunity />
                </LazyLoad>
              </div>
              {!hideComments && (
                <div className='storyComments-container'>
                  <LazyLoad offset={500} once>
                    <StoryComments story={story} />
                  </LazyLoad>
                </div>
              )}
              {!story.ads_disabled && <RevcontentWidget id={`${capitalizeAndDashCase(story.type)}_Comments-Bottom`} />}
            </div>
          </div>
          <div className='col col--sidebar'>
            <Sidebar adSettings={adSettings} />
          </div>
        </div>
      </div>
      <style jsx>{`
        .container {
          padding-left: 0;
          padding-right: 0;
        }
        .container > .row {
          display: grid;
          grid-template-columns: 1fr 300px;
          grid-column-gap: 30px;
          margin-left: 0;
          margin-right: 0;
        }
        .container > .row > .col {
          padding: 0;
        }
        @media (max-width: 480px) {
          .col--main {
            padding-left: 0;
            padding-right: 0;
          }
        }
        @media (min-width: 480px) {
          .container {
            padding-left: 15px;
            padding-right: 15px;
          }
        }
        @media (max-width: ${THEME.BREAKPOINTS.SM - 1}px) {
          .container > .row {
            grid-template-columns: 1fr;
          }
        }
        .story {
          background-color: white;
          box-shadow: ${THEME.BOX_SHADOW.DEFAULT};
          margin-bottom: 20px;
        }
        .story__title {
          padding: 8px 18px;
          font-size: 20px;
          line-height: 25px;
          font-weight: bold;
          letter-spacing: -0.1px;
          margin: 0;
        }
        @media (min-width: ${THEME.BREAKPOINTS.SM}px) {
          .story__title {
            font-size: 32px;
            line-height: 40px;
          }
        }
        .story__content {
          padding: 16px;
        }
        @media (min-width: ${THEME.BREAKPOINTS.SM}px) {
          .story__content {
            padding: 16px 32px;
          }
        }
        .storyComments-container {
          margin-top: 20px;
          margin-bottom: 20px;
          padding: 4px 16px;
          background-color: white;
          box-shadow: ${THEME.BOX_SHADOW.DEFAULT};
        }
      `}</style>
    </>
  )
}

export default observer(BlogDetail)
